<template>
  <div id="voice_agent_select" class="form_col gap-2">
    <span class="secondary_color text-[15px]">{{ sectionTitle.title }}</span>
    <CustomDropdown
      ref="dropdownRef"
      width="w-full"
      max-height="400px"
      gap="5px"
      position="start"
      dropdownCSS="p-0"
      @close="handleDropdownClose"
    >
      <template #trigger>
        <div class="input_medium">
          <div class="flex items-center flex-1 min-w-0">
            <template v-if="!selectedVoice">
              <span class="secondary_color">{{ sectionTitle.placeholder }}</span>
            </template>
            <template v-else>
              <VoiceItem
                :voice="selectedVoice"
                :is-playing="isSelectedVoicePlaying"
                :type="type"
                @play="handleTogglePlay"
              />
            </template>
          </div>

          <div class="flex items-center ml-2">
            <ThemeImage
              :icon="imageNames.BOTTOM_ARROW"
              alt="arrow"
              class="size-4"
            />
          </div>
        </div>
      </template>

      <template #default>
        <div class="flex flex-col">
          <div class="flex flex-col max-h-[300px]">
            <div class="flex flex-col py-3 px-2 border_bottom">
              <CustomInput
                format="text"
                v-model="searchQuery"
                placeholder="Search"
                type="primary"
                :icon="imageNames.SEARCH_VARIANT1"
                alt="search_icon"
                auto-focus
              />
            </div>

            <div
              v-if="filteredVoices.length"
              class="flex flex-col pl-2 py-2 overflow-y-scroll custom_scrollbar max-h-[200px]"
            >
              <div
                v-for="voice in filteredVoices"
                :key="voice.voice_id"
                :class="[
                  'dropdown_item flex-shrink-0 flex items-center flex-row px-3 py-2 rounded-lg',
                  { dropdown_item_selected: isVoiceSelected(voice.voice_id) }
                ]"
                @click="handleVoiceSelect(voice)"
              >
                <VoiceItem
                  :voice="voice"
                  :is-playing="isVoicePlaying(voice.voice_id)"
                  :type="type"
                  @play="handleTogglePlay"
                />
              </div>
            </div>

            <div
              v-else
              class="flex flex-col items-center justify-center py-6 px-4"
            >
              <ThemeImage
                :icon="imageNames.EMPTY_SCREEN_GREY"
                alt="empty_icon"
                class="size-16"
              />
              <span class="text-sm secondary_color text-center mt-2">
                No results found!
              </span>
            </div>
          </div>
        </div>
      </template>
    </CustomDropdown>
  </div>
</template>

<script setup>
import { ref, computed, watch } from "vue";
import CustomDropdown from "@/src/components/CustomDropdown.vue";
import CustomInput from "@/src/components/CustomInput.vue";
import ThemeImage from "@/src/components/ThemeImage.vue";
import VoiceItem from "@/src/components/voice_agents/VoiceItem.vue";
import { imageNames } from "@/src/constants/imageConstants.js";
import { defaultVoiceId, defaultVoice } from "@/src/constants/voiceAgentConstants.js";

const props = defineProps({
  voices: {
    type: Array,
    default: () => [],
  },
  selectedVoice: {
    type: Object,
    default: null,
  },
  type: {
    type: String,
    default: "voice",
  },
});

const emit = defineEmits(["update:selectedVoice"]);

const dropdownRef = ref(null);
const playingVoiceId = ref(null);
const audioElement = ref(null);
const searchQuery = ref("");

const sectionTitle = computed(() => {
  return {
    title: props.type === "language" ? "Language" : "Voice",
    placeholder: props.type === "language" ? "Select a language" : "Select a voice",
  };

});

const allVoices = computed(() => {
  try {
    if (props.type === "language") {
      return props.voices || [];
    }

    if (!props.voices || props.voices.length === 0) {
      return [defaultVoice];
    }
    const hasDefaultVoice = props.voices.some(v => v.voice_id === defaultVoiceId);
    if (hasDefaultVoice) {
      return props.voices;
    }
    return [defaultVoice, ...props.voices];
  } catch (error) {
    console.error("Error getting all voices:", error);
    return props.type === "language" ? [] : [defaultVoice];
  }
});

const selectedVoice = computed(() => {
  try {
    if (!props.selectedVoice) return null;
    return allVoices.value.find((v) => v.voice_id === props.selectedVoice.voice_id) || props.selectedVoice;
  } catch (error) {
    console.error("Error getting selected voice:", error);
    return props.selectedVoice;
  }
});

const isSelectedVoicePlaying = computed(() => {
  try {
    return playingVoiceId.value === selectedVoice.value?.voice_id;
  } catch (error) {
    console.error("Error checking if selected voice is playing:", error);
    return false;
  }
});

const filteredVoices = computed(() => {
  try {
    if (!searchQuery.value) return allVoices.value;
    
    const query = searchQuery.value.toLowerCase().trim();
    return allVoices.value.filter(voice => 
      voice.voice_name?.toLowerCase().includes(query) ||
      voice.accent?.toLowerCase().includes(query) ||
      voice.gender?.toLowerCase().includes(query)
    );
  } catch (error) {
    console.error("Error filtering voices:", error);
    return allVoices.value;
  }
});

const isVoiceSelected = (voiceId) => {
  try {
    return props.selectedVoice?.voice_id === voiceId;
  } catch (error) {
    console.error("Error checking voice selection:", error);
    return false;
  }
};

const isVoicePlaying = (voiceId) => {
  try {
    return playingVoiceId.value === voiceId;
  } catch (error) {
    console.error("Error checking voice playing:", error);
    return false;
  }
};

const handleVoiceSelect = (voice) => {
  try {
    emit("update:selectedVoice", voice);
    searchQuery.value = "";
    if (dropdownRef.value) {
      dropdownRef.value.closeDropdown();
    }
  } catch (error) {
    console.error("Error selecting voice:", error);
  }
};

const handleTogglePlay = (voice) => {
  try {
    if (!voice) return;
    
    if (playingVoiceId.value === voice.voice_id) {
      if (audioElement.value) {
        audioElement.value.pause();
        audioElement.value = null;
      }
      playingVoiceId.value = null;
    } else {
      if (audioElement.value) {
        audioElement.value.pause();
        audioElement.value = null;
      }
      
      if (voice.preview_audio_url) {
        audioElement.value = new Audio(voice.preview_audio_url);
        audioElement.value.play();
        playingVoiceId.value = voice.voice_id;
        
        audioElement.value.onended = () => {
          playingVoiceId.value = null;
          audioElement.value = null;
        };
      }
    }
  } catch (error) {
    console.error("Error playing voice:", error);
  }
};

const handleDropdownClose = () => {
  searchQuery.value = "";
};

const updateVoiceFromAPI = (newVoices) => {
  try {
    if (props.type === "voice" && newVoices && newVoices.length > 0 && props.selectedVoice?.voice_id === defaultVoiceId) {
      const apiMiaVoice = newVoices.find(v => v.voice_id === defaultVoiceId);
      if (apiMiaVoice) {
        emit("update:selectedVoice", apiMiaVoice);
      }
    }
  } catch (error) {
    console.error("Error updating default voice:", error);
  }
};

watch(() => props.voices, updateVoiceFromAPI);

</script>
